import React, { useState, useEffect } from 'react';
import { motion} from 'motion/react';
import { ShareIcon, BellIcon } from '@/components/icons';
import ShareModal from '@/components/ShareModal';
import { useAuth } from '@/hooks/useAuth';
import { likeService } from '@/features/likes/services/likeService';
import { authService } from '@/features/user/services/authService';
import type { LikeableEntityType } from '@/features/likes/types/likeTypes';

interface ActionButtonsProps {
  entityId: string;
  entityName: string;
  entityType: LikeableEntityType;
  showBell?: boolean;
  className?: string;
  initialLiked?: boolean;
}

/**
 * Reusable action buttons component (Like, Share, Bell)
 * For use in Artist and Event pages
 */
const ActionButtons: React.FC<ActionButtonsProps> = ({
  entityId,
  entityName,
  entityType,
  showBell = true,
  className = '',
  initialLiked = false,
}) => {
  const { isAuthenticated, refetch: refetchAuth } = useAuth();
  const [isLiked, setIsLiked] = useState(initialLiked);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isBellActive, setIsBellActive] = useState(false);
  const [isBellAnimating, setIsBellAnimating] = useState(false);

  // Check initial like status on mount
  useEffect(() => {
    const checkLikeStatus = async () => {
      if (!entityId || !isAuthenticated) return;
      
      try {
        const response = await likeService.checkLikes({
          entity_ids: [entityId],
          entity_type: entityType,
        });
        
        // Check if the entity is in the liked_map
        if (response.liked_map && response.liked_map[entityId]) {
          setIsLiked(true);
        }
      } catch (error) {
        console.error('Failed to check like status:', error);
      }
    };

    checkLikeStatus();
  }, [entityId, entityType, isAuthenticated]);

  const handleLikeClick = async () => {
    // If already registering, ignore clicks
    if (isRegistering) return;

    // Immediately update UI (optimistic update)
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);

    // If unauthenticated, register as guest first
    if (!isAuthenticated) {
      try {
        setIsRegistering(true);
        await authService.registerGuest();
        await refetchAuth();
      } catch (error) {
        console.error('Failed to register guest:', error);
        // Revert optimistic update on registration failure
        setIsLiked(isLiked);
        setIsRegistering(false);
        return;
      } finally {
        setIsRegistering(false);
      }
    }

    // Fire-and-forget API call
    likeService.toggleLike({
      entity_id: entityId,
      entity_type: entityType,
    }).catch((error) => {
      console.error('Failed to toggle like:', error);
    });
  };

  const handleBellClick = () => {
    setIsBellActive(!isBellActive);
    setIsBellAnimating(true);
    setTimeout(() => setIsBellAnimating(false), 600);
  };

  return (
    <>
      <div className={`flex items-center gap-2 ${className}`}>
        {/* Like Button */}
        <button
          onClick={handleLikeClick}
          className="p-3 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-200"
        >
          <motion.div
            animate={{ scale: isLiked ? [1, 1.3, 1] : 1 }}
            transition={{ duration: 0.3 }}
            className={isLiked ? 'text-red-500' : 'text-white'}
          >
            {isLiked ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
              </svg>
            )}
          </motion.div>
        </button>

        {/* Bell Button */}
        {showBell && (
          <button
            onClick={handleBellClick}
            className="p-3 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-200"
          >
            <motion.div
              animate={isBellAnimating ? { rotate: [0, 15, -15, 10, -10, 5, -5, 0] } : {}}
              transition={{ duration: 0.6 }}
              className={isBellActive ? 'text-yellow-400' : 'text-white'}
            >
              <BellIcon className={`w-6 h-6 ${isBellActive ? 'fill-current' : ''}`} />
            </motion.div>
          </button>
        )}

        {/* Share Button */}
        <button
          onClick={() => setIsShareModalOpen(true)}
          className="p-3 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-200"
        >
          <ShareIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        entityName={entityName}
      />
    </>
  );
};

export default ActionButtons;
